import React, { useEffect, useMemo, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Calendar, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid
} from 'recharts';

const DailyPaymentsChart = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [uniquePlans, setUniquePlans] = useState([]); // [{key, label}]
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const startDateTime = `${dateRange.start}T00:00:00.000Z`;
      const endDateTime = `${dateRange.end}T23:59:59.999Z`;
      const url = new URL(`${API_BASE_URL}/api/admin/reports/payments/daily-summary`);
      url.searchParams.set('start_date', startDateTime);
      url.searchParams.set('end_date', endDateTime);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to load daily payments summary');
      const json = await resp.json();

      // Build plan series keys from all days
      const planKeySet = new Map(); // plan_name -> true
      json.forEach(d => {
        (d.plans || []).forEach(p => {
          const label = p.plan_name || 'Unknown';
          if (!planKeySet.has(label)) planKeySet.set(label, true);
        });
      });
      const planKeys = Array.from(planKeySet.keys());
      setUniquePlans(planKeys.map(k => ({ key: k, label: k })));

      // Expand data rows with counts per plan name
      const rows = json.map(d => {
        const row = { date: d.date, total_count: d.total_count };
        (d.plans || []).forEach(p => {
          const label = p.plan_name || 'Unknown';
          row[label] = p.count || 0;
        });
        // Ensure missing plan keys are zero for stacked bars
        planKeys.forEach(k => { if (row[k] == null) row[k] = 0; });
        return row;
      });
      setData(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateRange.start, dateRange.end]);

  const colors = useMemo(() => {
    const palette = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#84cc16','#a855f7','#ea580c','#0ea5e9'];
    const map = {};
    uniquePlans.forEach((p, idx) => { map[p.key] = palette[idx % palette.length]; });
    return map;
  }, [uniquePlans]);

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Card.Title>Daily Payments (Counts by Plan)</Card.Title>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200">
              <Calendar className="h-4 w-4 text-gray-500" />
              <label className="text-sm text-gray-700">From</label>
              <input type="date" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))} className="border border-gray-300 rounded px-2 py-1 text-sm" />
              <label className="text-sm text-gray-700">To</label>
              <input type="date" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))} className="border border-gray-300 rounded px-2 py-1 text-sm" />
            </div>
            <Button variant="secondary" onClick={fetchData} icon={<RefreshCw size={14} />}>Refresh</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : (
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                {uniquePlans.map(p => (
                  <Bar key={p.key} dataKey={p.key} yAxisId="left" stackId="plans" fill={colors[p.key]} />
                ))}
                <Line type="monotone" yAxisId="right" dataKey="total_count" stroke="#111827" strokeWidth={2} name="Total" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

export default DailyPaymentsChart;